/**
 * Explicit success/failure, for operations that fail as part of normal use.
 *
 * Reserved for *expected* failures: a rejected reparent that would create a cycle, an upload that
 * exceeds the size limit, a stale revision. Programming errors still throw — wrapping those in a
 * `Result` only moves the crash somewhere less informative.
 *
 * The point is that the compiler forces the caller to consider the failure. In an editor where
 * every operation touches the user's only copy of their thinking, an unhandled failure path is
 * how work disappears without a message.
 */
export function ok(value) {
    return { ok: true, value };
}
export function err(error) {
    return { ok: false, error };
}
export function isOk(result) {
    return result.ok;
}
export function isErr(result) {
    return !result.ok;
}
/** Applies `fn` to a success value, passing failures through untouched. */
export function mapResult(result, fn) {
    return result.ok ? ok(fn(result.value)) : result;
}
/** Chains an operation that can itself fail, short-circuiting on the first failure. */
export function andThen(result, fn) {
    return result.ok ? fn(result.value) : result;
}
/** Unwraps a success or substitutes a fallback. Never throws. */
export function unwrapOr(result, fallback) {
    return result.ok ? result.value : fallback;
}
/**
 * Unwraps a success or throws.
 *
 * For call sites that have already established the result is a success — a test, or a branch
 * guarded by `isOk`. Using it to avoid handling a failure defeats the purpose of the type.
 */
export function unwrap(result) {
    if (result.ok)
        return result.value;
    throw new Error(`unwrap called on a failed Result: ${JSON.stringify(result.error)}`);
}
/** Collects an array of results into a result of an array, failing on the first error. */
export function collect(results) {
    const values = [];
    for (const result of results) {
        if (!result.ok)
            return result;
        values.push(result.value);
    }
    return ok(values);
}
//# sourceMappingURL=result.js.map